import { memo, useRef, useState } from 'react'

import { Nullable } from '@types'

import { Shortlist } from '@modules/shortlist/types'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'

import { DeleteIconWrapper, ShortlistItemName, ShortlistItemTextField, ShortlistItemWrapper } from './index.styles'
import { EditShortlistsPopupProps } from './types'

export const ShortlistItemRow = memo(
  ({
    shortlist,
    shortlistName,
    value,
    onSwitch,
    onDelete,
    onChange,
    validationError,
    isInEditMode,
    setIsInEditMode,
    hasMultipleShortlists,
  }: {
    onDelete: EditShortlistsPopupProps['onDelete']
    onSwitch: (id: Shortlist, newStatus: boolean) => void
    onChange: (id: Shortlist, newName: string) => void
    value: boolean
    shortlistName: Shortlist['name']
    shortlist: Shortlist
    validationError?: string
    isInEditMode?: boolean
    setIsInEditMode?: (value: boolean, id: Shortlist['id']) => void
    hasMultipleShortlists: boolean
  }) => {
    const initialName = useRef(shortlistName)
    const [animateBorder, setAnimateBorder] = useState<Nullable<boolean>>(null)
    const handleDelete = () => onDelete(shortlist.id)
    const handleSwitch = () => onSwitch(shortlist, !value)

    const handleChange = (str: string) => {
      setAnimateBorder(null)
      onChange(shortlist, str || '')
    }

    const handleSubmit = () => {
      if (validationError) {
        return
      }

      if (initialName.current?.trim() !== shortlistName?.trim() && animateBorder === null) {
        setAnimateBorder(true)
        setTimeout(() => setAnimateBorder(false), 5000)
      }

      setIsInEditMode?.(false, shortlist.id)
    }

    const handleKeyPress = (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return
      }

      handleSubmit()
    }

    return (
      <ShortlistItemWrapper>
        <DeleteIconWrapper onClick={handleDelete}>
          <DeleteIcon color="primary" />
        </DeleteIconWrapper>
        {isInEditMode ? (
          <ShortlistItemTextField
            autoFocus
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target?.value || '')}
            onBlur={handleSubmit}
            onKeyDown={handleKeyPress}
            value={shortlistName}
            error={!!validationError}
          />
        ) : (
          <ShortlistItemName
            onClick={() => setIsInEditMode?.(true, shortlist.id)}
            className={animateBorder ? 'border-animation' : ''}
          >
            <Typography variant="body1">{shortlistName}</Typography>
          </ShortlistItemName>
        )}
        <Switch
          edge="end"
          size="small"
          checked={value ?? false}
          onClick={handleSwitch}
          onKeyPress={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && hasMultipleShortlists) {
              handleSwitch()
            }
          }}
          disabled={!hasMultipleShortlists}
        />
      </ShortlistItemWrapper>
    )
  },
)
